import React, { useState, useEffect, ChangeEvent } from 'react';
import InputWithLine from 'Components/UI/InputWithLine/InputWithLine';
import { TransitInfo } from './TransitInfo/TransitInfo';
import { LetterSendButton } from './LetterSendButton/LetterSendButton';
import { Letter } from './Letter/Letter';
import { useSelector } from 'react-redux';
import { selectMilesOut } from 'Redux/Location/slice';
import { selectCurrentOrder } from 'Redux/Order/slice';
import { capitalizeFirstLetter, stringToMiles } from 'Shared/Utils';
import styles from './OrderCalc.module.scss';

type MilesItem = {
  name: 'empty' | 'loaded' | 'broker' | 'driver' | 'revenue';
  totalMiles: number;
  price: number;
  pricePerMile: number;
};

type MilesState = {
  empty: MilesItem;
  loaded: MilesItem;
  broker: MilesItem;
  driver: MilesItem;
  revenue: MilesItem;
};

export type CalcInputData = Pick<MilesItem, 'name' | 'totalMiles'>;

export type CalcInputHandler = (
  event: ChangeEvent<HTMLInputElement>,
  data: CalcInputData,
  isPerMileType?: boolean,
) => void;

export const OrderCalc: React.FC = () => {
  const currentOrder = useSelector(selectCurrentOrder);
  const [letter, setLetter] = useState('');
  const milesOut = useSelector(selectMilesOut);
  const [miles, setMiles] = useState<MilesState>({
    empty: {
      name: 'empty',
      totalMiles: 0,
      price: 0,
      pricePerMile: 0,
    },
    loaded: {
      name: 'loaded',
      totalMiles: 0,
      price: 0,
      pricePerMile: 0,
    },
    broker: {
      name: 'broker',
      totalMiles: 0,
      price: 0,
      pricePerMile: 0,
    },
    driver: {
      name: 'driver',
      totalMiles: 0,
      price: 0,
      pricePerMile: 0,
    },
    revenue: {
      name: 'revenue',
      totalMiles: 0,
      price: 0,
      pricePerMile: 0,
    },
  });
  const [comment, setComment] = useState('Your comments...');

  const calcInputHandler: CalcInputHandler = (
    { target },
    { name, totalMiles },
    isPerMileType = false,
  ) => {
    if (['revenue', 'broker'].includes(name)) {
      return;
    }
    const value = Math.abs(parseFloat(target.value));

    if (value && isPerMileType) {
      setMiles((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          price: totalMiles && value * totalMiles,
          pricePerMile: value,
        },
      }));
    } else {
      setMiles((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          price: value,
          pricePerMile: totalMiles && Number((value / totalMiles).toFixed(2)),
        },
      }));
    }
  };

  const letterChangeHandler = (newLetter: string) => {
    setLetter(newLetter);
  };

  const commentChangeHandler = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setComment(event.target.value);
  };

  const commentResetter = () => {
    setComment('');
  };

  // update driver miles out
  useEffect(() => {
    setMiles((prevState) => ({
      ...prevState,
      empty: {
        ...prevState.empty,
        totalMiles: milesOut?.distanceInMi || 0,
      },
    }));
  }, [milesOut]);

  // update load miles
  useEffect(() => {
    const loadedMiles =
      stringToMiles(currentOrder?.line) || currentOrder?.transit_distance || 0;

    setMiles((prevState) => ({
      ...prevState,
      loaded: {
        ...prevState.loaded,
        totalMiles: loadedMiles,
      },
      driver: {
        ...prevState.driver,
        totalMiles: loadedMiles,
      },
    }));
  }, [currentOrder]);

  // broker prices calculation
  useEffect(() => {
    if (
      miles.empty.price > 0 &&
      miles.empty.pricePerMile > 0 &&
      miles.loaded.price > 0 &&
      miles.loaded.pricePerMile > 0
    ) {
      setMiles((prevState) => ({
        ...prevState,
        broker: {
          ...prevState.broker,
          price: prevState.empty.price + prevState.loaded.price,
          pricePerMile:
            prevState.empty.pricePerMile + prevState.loaded.pricePerMile,
        },
      }));
    }
  }, [miles.empty, miles.loaded]);

  // revenue calculation
  useEffect(() => {
    if (
      miles.broker.price > 0 &&
      miles.broker.pricePerMile > 0 &&
      miles.driver.price > 0 &&
      miles.driver.pricePerMile > 0
    ) {
      setMiles((prevState) => {
        const revenuePrice =
          prevState.broker.price > prevState.driver.price
            ? prevState.broker.price - prevState.driver.price
            : 0;

        return {
          ...prevState,
          revenue: {
            ...prevState.revenue,
            price: revenuePrice,
            pricePerMile:
              revenuePrice > 0
                ? Math.round((revenuePrice / prevState.broker.price) * 100)
                : 0,
          },
        };
      });
    }
  }, [miles.broker, miles.driver]);

  return (
    <>
      <div className={styles.calc}>
        <div className={styles.container}>
          <div className={styles.inputsBox}>
            <ul>
              {Object.entries(miles).map(
                ([key, { name, totalMiles, price, pricePerMile }]) => {
                  return (
                    <li key={key} className={styles.inputsBox__row}>
                      <span className={styles.inputsBox__row__title}>
                        {capitalizeFirstLetter(key)}{' '}
                        {key !== 'revenue' && 'miles'}
                      </span>
                      <InputWithLine
                        value={price}
                        onChange={calcInputHandler}
                        data={{ name, totalMiles }}
                      />
                      <InputWithLine
                        value={pricePerMile}
                        onChange={calcInputHandler}
                        data={{ name, totalMiles }}
                        isPerMileType={true}
                      />
                    </li>
                  );
                },
              )}
            </ul>
            <LetterSendButton
              letter={letter}
              comment={comment}
              commentResetter={commentResetter}
              brokerPrice={miles.broker.price}
              driverPrice={miles.driver.price}
              buttonName="Place bid"
            />
          </div>

          <TransitInfo />
        </div>
      </div>

      <Letter
        brokerPrice={miles.broker.price}
        letter={letter}
        letterChangeHandler={letterChangeHandler}
        comment={comment}
        commentChangeHandler={commentChangeHandler}
      />
    </>
  );
};
